import { useMemo } from 'react';
import { WebsiteEditorCustomBlocksConfig, getEventTypeConfig } from '@config';
import { QuickTranslate } from '@shared/core/i18n/useTranslation';
import { useEventInfo } from '@shared/utils/eventInfo';

/**
 * Hook that returns the resolved website editor custom blocks configuration
 * for the current event, automatically handling event type and translation.
 *
 * @param tq - The translation function from useTranslation hook
 * @returns The resolved custom blocks config, or null if event type is not available
 *
 * @example
 * ```tsx
 * const { tq } = useTranslation('VIPCustomComponent');
 * const customBlocksConfig = useWebsiteEditorCustomBlocksConfig(tq);
 * const vipTitle = customBlocksConfig?.vip.componentTitle;
 * ```
 */
export const useWebsiteEditorCustomBlocksConfig = (tq: QuickTranslate): WebsiteEditorCustomBlocksConfig | null => {
  const { eventInfo } = useEventInfo();

  return useMemo(() => {
    return eventInfo?.eventType ? getEventTypeConfig(eventInfo.eventType).websiteEditorCustomBlocks(tq) : null;
  }, [eventInfo?.eventType, tq]);
};
